import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { weekdayMap } from "./constants";
import { AppDispatch } from "../../redux/store";
import { showNotification } from "../../redux/slices/notificationSlice";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const tzAbbr: Record<string, string> = {
  "America/New_York": "ET",
  "America/Chicago": "CT",
  "America/Denver": "MT",
  "America/Los_Angeles": "PT",
  "America/Vancouver": "PT",
  "America/Toronto": "ET",
  "America/Phoenix": "MST",
  "America/Anchorage": "AKT",
  "America/Halifax": "AST",
  "America/Santo_Domingo": "AST",
  "America/Sao_Paulo": "BRT",
  // other timezones
  "Europe/London": "BST",
  "Asia/Singapore": "SGT",
  "UTC": "UTC",
};

export const mapReduxTimeToString = (byHour: number, byMinute: number) => {
  return dayjs()
    .hour(byHour)
    .minute(byMinute)
    .format("h:mm A");
};

export const formatCadence = (cadence, by_week_day, by_month_day, by_month, by_year, by_hour, by_minute, timezone) => {
  const baseTime = mapReduxTimeToString(by_hour, by_minute);
  const abbr = tzAbbr[timezone] ?? timezone;

  switch (cadence) {
    case "DAILY":
      return `Daily at ${baseTime} ${abbr}`;

    case "WEEKLY":
      return `Weekly on ${weekdayMap[by_week_day ?? 1]}s, ${baseTime} ${abbr}`;

    case "MONTHLY":
      return `Monthly on ${getOrdinal(by_month_day)}, ${baseTime} ${abbr}`;

    case "ONCE": {
      const formattedDate = dayjs()
        .year(by_year)
        .month(by_month - 1) // dayjs month is 0–11, backend sends 1–12
        .date(by_month_day)
        .format("D MMM");
      return `Scheduled for ${formattedDate},  ${baseTime} ${abbr}`;
    }

    default:
      return cadence;
  }
};

export const getNextTimeSlot = (interval: number = 30) => {
  const now = dayjs();
  const minutes = now.minute();
  const remainder = interval - (minutes % interval);
  const next = now.add(remainder, "minute");
  return next.format("h:mm A");
};

export const getTimezone = () => {
  return dayjs.tz.guess();
};

export const generateTimes = (interval = 30, selectedDate?: Dayjs | null) => {
  const times: string[] = [];
  const now = dayjs();
  const date = selectedDate ? dayjs(selectedDate) : null;

  const start = (date ?? now).startOf("day");
  const end = (date ?? now).endOf("day");
  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    if (
      !date
      || !date.isSame(now, "day")
      || current.isAfter(now)
    ) {
      times.push(current.format("h:mm A"));
    }
    current = current.add(interval, "minute");
  }
  return times;
};

export const notifyTaskScheduleError = (
  dispatch: AppDispatch,
  limit: number,
  error?: string,
  planName?: string,
) => {
  const isLimitError
    = error
    === "User has reached the maximum number of active task schedules for free plan";

  const message = isLimitError
    ? `You’ve reached the limit of ${limit} active tasks${
      planName ? ` for your ${planName} plan` : ""
    }. Pause an active task to schedule this one.`
    : "Something went wrong while updating the task.";

  dispatch(
    showNotification({
      title: "This task cannot be scheduled",
      message,
      severity: "error",
      horizontal: "right",
    }),
  );
};
